import './App.css';
import './lib/skewt/skewt.css';
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation,
} from "react-router-dom";
import HomePage from "./pages/HomePage";
import StationDashboard from "./pages/StationDashboard";
import {WeatherDataContextProvider} from "./contexts/WeatherDataContextProvider";
import {useMediaQuery} from "react-responsive";
import {useEffect, useState} from "react";
import i18n from './i18n';
import {I18nextProvider, useTranslation} from "react-i18next";
import {Helmet} from "react-helmet";
import {ThemeProvider} from "./contexts/ThemeContext";
import NotFound from "./pages/NotFound";
import ForecastPage from "./pages/ForecastPage";
import ForecastProvider from "./contexts/ForecastContextProvider";
import NavigationHeader from "./components/NavigationHeader";
import GlobalSearch from "./components/GlobalSearch";
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DashboardActionsRoot } from './contexts/DashboardActionsContext';


// Disable the browser's default scroll-restoration so a hard refresh always
// lands at the top of the page (otherwise long pages like StationDashboard
// reopen mid-scroll). React Router will manage scroll position itself via
// <ScrollToTop/> below.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
}

// Scrolls to the top whenever the route's pathname changes, AND on initial
// mount — together with `scrollRestoration = 'manual'` above, this guarantees
// every page load starts at the top regardless of previous scroll position.
const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

const AppContent = () => {
    const {t} = useTranslation();
    const [showSearch, setShowSearch] = useState(false);

    return (
        <>
            <Helmet>
                <title>{t('Vazduhoplovni savez Srbije | Meteo')}</title>
                <meta
                    name="description"
                    content={t('Vazduhoplovni savez Srbije | Meteo')}
                />
                <meta property="og:image" content="/assets/images/vss.jpeg"/>
                <meta name="keywords"
                      content="VSS, Vazduhoplovni savez Srbije, Vazduhoplovni Savez Srbije, Meteo"/>
            </Helmet>
            <BrowserRouter>
                <ScrollToTop />
                <NavigationHeader onSearchOpen={() => setShowSearch(true)} />
                <GlobalSearch
                    isOpen={showSearch}
                    onClose={() => setShowSearch(false)}
                    onOpen={() => setShowSearch(true)}
                />
                <main>
                    <Routes>
                        <Route path="/" element={<HomePage/>}/>
                        <Route path="/forecast" element={<ForecastPage/>}/>
                        <Route path="/site/:takeoffSiteSlug" element={<StationDashboard/>}/>
                        <Route path='*' element={<NotFound/>}/>
                    </Routes>
                </main>
                <ToastContainer/>
            </BrowserRouter>
        </>
    );
};

function App() {
    const systemPrefersDark = useMediaQuery({query: '(prefers-color-scheme: dark)'});

    useEffect(() => {
        localStorage.setItem('systemPrefersDark', systemPrefersDark);
    }, [systemPrefersDark])

    return (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider>
                <WeatherDataContextProvider>
                    <ForecastProvider>
                        <DashboardActionsRoot>
                            <AppContent />
                        </DashboardActionsRoot>
                    </ForecastProvider>
                </WeatherDataContextProvider>
            </ThemeProvider>
        </I18nextProvider>
    );
}

export default App;
